import React from 'react';
import './index.css';
import { Layout, Typography, Card, Divider, Collapse, Image, List } from 'antd';
import {
  HomeOutlined,
  FileSearchOutlined,
  BarChartOutlined,
  DatabaseOutlined,
  ApiOutlined,
  QuestionCircleOutlined,
  LineChartOutlined,
  DownloadOutlined
} from '@ant-design/icons';

const { Content } = Layout;
const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const images = {
  home1: require('./image/home1.png'),
  home2: require('./image/home2.png'),
  home3: require('./image/home3.png'),
  home4: require('./image/home4.png'),
  sample1: require('./image/sample1.png'),
  sample2: require('./image/sample2.png'),
  sample3: require('./image/sample3.png'),
  sample4: require('./image/sample4.png'),
  sample5: require('./image/sample5.png'),
  taxa1: require('./image/taxa1.png'),
  taxa2: require('./image/taxa2.png'),
  taxa3: require('./image/taxa3.png'),
  taxa4: require('./image/taxa4.png'),
  taxa5: require('./image/taxa5.png'),
  species1: require('./image/species1.png'),


  data1: require('./image/data1.png'),
  data2: require('./image/data2.png'),
  homeMap: require('./image/home_map.png'),
  speciesUpset: require('./image/species_upset.png'),
  pathogenicMain: require('./image/pathogenic_main.png'),
  pathogenicDetail: require('./image/pathogenic_detail.png')
};

const Help = () => {
  return (
    <Layout className="layout">
      <Content style={{ padding: '0 50px' }}>
        <div className="site-layout-content">
          <Title level={2} style={{ textAlign: 'center', marginBottom: '30px' }}>
            <QuestionCircleOutlined /> LRMrepo User Guide
          </Title>

          <Card bordered={false} style={{ marginBottom: '24px' }}>
            <Paragraph>
              <Text strong>LRMrepo</Text> is a curated metagenomic database providing standardized annotations and analysis results for long-read metagenomic data.
              The system consists of four main modules across six pages, offering comprehensive data access and analysis capabilities for researchers.
            </Paragraph>
          </Card>

          <Collapse bordered={false} defaultActiveKey={['1']} accordion>
            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <HomeOutlined /> Home Page
                </Title>
              }
              key="1"
            >
              <Divider orientation="left">1. Database Overview</Divider>
              <Paragraph>
                The Home page is the main entry point for accessing the database, providing the following core features:
              </Paragraph>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Database statistics: number of long-read metagenomic samples/runs, projects, and associated organisms',
                  'Key features:',
                  '- Manually curated metadata',
                  '- Samples grouped by phenotype (animal, human, health & disease)',
                  '- Interactive tools for fast data browsing'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.home1} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">2. Data Query</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Advanced query builder (DemoQueryBuilder) supporting multi-criteria combined queries',
                  'Two query modes:',
                  '- Samples/Runs mode',
                  '- Projects mode'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.home2} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">3. Data Display and Operations</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Table display of query results, supporting:',
                  '- Column hide/show customization (via "Hide Columns" selector)',
                  '- Sorting (click column headers)',
                  '- Pagination',
                  '- Link navigation to detailed sample pages',
                  'Data export: export query results as Excel files'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.home3} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">4. Navigation</Divider>
              <Paragraph>Provides links to other key pages:</Paragraph>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Data page (/data)',
                  'Help page (/help)',
                  'Taxa page (/taxa)'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.home4} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">5. Global Distribution of Sampling Locations</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'World map showing the geographic distribution of all sample collection sites',
                  'Countries are color-coded by sample count (green gradient: dark = more samples)',
                  'Hover over any country to see the exact sample count',
                  'Right-side panel displays the Top 15 countries ranked by sample count',
                  'Supports zoom and pan for detailed exploration'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.homeMap} style={{ maxWidth: '100%', margin: '16px 0' }} />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <FileSearchOutlined /> Sample Detail Page
                </Title>
              }
              key="2"
            >
              <Paragraph>
                The Sample Detail page provides detailed analysis data and visualizations for a single sample.
              </Paragraph>

              <Divider orientation="left">1. Sample Metadata</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Displays the unique sample identifier (Run ID)',
                  'Shows sample metadata information'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.sample1} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">2. Species Relative Abundance</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Pie chart showing relative abundance of species in the sample (top 10 genera + other)',
                  'Bar chart displaying the same data for easier comparison between genera'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.sample2} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">3. Bin Quality Distribution</Divider>
              <Paragraph>Bar chart showing genome bin quality distribution:</Paragraph>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'High Quality',
                  'Medium Quality',
                  'Low Quality'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.sample3} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">4. Pathopoiesis Distribution</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Bar chart and pie chart showing the proportion of pathogenic and non-pathogenic bins'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.sample4} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">5. Data Download</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Download options in multiple formats:',
                  '- Download .tab format file',
                  '- Download .tsv format file',
                  '- Download .csv format file (compatible with Excel etc.)'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.sample5} style={{ maxWidth: '100%', margin: '16px 0' }} />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <BarChartOutlined /> Taxa Page
                </Title>
              }
              key="3"
            >
              <Paragraph>
                The Taxa page provides taxonomic classification browsing and search functionality.
              </Paragraph>

              <Divider orientation="left">1. Taxonomic Classification Levels</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Switch between six classification levels: Species, Genus, Family, Order, Class, Phylum',
                  'Display statistics for each level',
                  '72 habitat columns show species count per habitat for each level (Species level: presence 1/0; higher levels: aggregated species count)',
                  'Hide Columns selector allows filtering visible columns',
                  'Species names are clickable to view UpSet plot for habitat co-occurrence analysis'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.taxa1} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">2. Search</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Search by taxonomy name at the current classification level',
                  'Paginated search results'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.taxa2} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">3. Statistics</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Total number of valid samples in the database',
                  'Total number of species and genera',
                  'Number of species/genera meeting criteria (present in 2 or more samples with median relative abundance >= 0.01%)'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.taxa3} style={{ maxWidth: '100%', margin: '16px 0' }} />
              <Image src={images.taxa4} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">4. External Links</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Quick access links to NCBI and ENA databases',
                  'Species names are clickable to view details'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.taxa5} style={{ maxWidth: '100%', margin: '16px 0' }} />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <LineChartOutlined /> Species Detail Page
                </Title>
              }
              key="4"
            >
              <Paragraph>
                The Species Detail page provides detailed analysis and visualization for a single species.
              </Paragraph>

              <Divider orientation="left">1. Species Information</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Displays species name and its occurrence count in the database',
                  'Quick access links to NCBI and ENA databases'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.species1} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">2. UpSet Plot</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Left bars (horizontal): total number of species found in each of the 72 habitats',
                  'Top bars (vertical): number of species sharing each specific habitat combination',
                  'Dot matrix with vertical lines: each column represents a habitat combination; dots indicate which habitats belong to each combination',
                  'A single isolated dot represents species unique to one habitat; linked dots represent species shared across multiple habitats',
                  'Orange bars: left bars indicate habitats where the current species was found; top bar indicates the combination this species belongs to',
                  'The full habitat list for the current species is displayed above with the total count',
                  'Only the top 50 habitat combinations by species count are shown'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.speciesUpset} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">3. External Database Links</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'One-click navigation to NCBI and ENA for more taxonomic information'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <DatabaseOutlined /> Data Page
                </Title>
              }
              key="5"
            >
              <Paragraph>
                The Data page provides summary information and detailed data for projects and runs.
              </Paragraph>

              <Divider orientation="left">1. Data Overview</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Database-wide statistics:',
                  '- Total number of projects and samples/runs',
                  '- Breakdown by sequencing technology (Oxford Nanopore and Pacific Biosciences)',
                  '- Number of samples and projects with processed raw data',
                  '- Number of samples and projects with available microbial abundance data'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.data1} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">2. Projects Table</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Table displaying all project information:',
                  '- Project name (BioProject)',
                  '- Manually curated status (curatedProjects)',
                  '- Data accuracy flag (WhetherCorrect)',
                  '- Total number of associated runs (totalRuns)',
                  'Features:',
                  '- Project name search',
                  '- Expandable rows to view project details'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.data2} style={{ maxWidth: '100%', margin: '16px 0' }} />

              <Divider orientation="left">3. Runs Data</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'View associated run data by expanding project rows:',
                  '- Run ID',
                  '- Paper Name',
                  '- Project Description',
                  '- BioSample ID',
                  '- Sequencing Instrument',
                  '- Organism',
                  '- Release Date'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />

              <Divider orientation="left">4. QC Status</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Data quality control status indicators:',
                  '- QC passed and loaded into database',
                  '- QC failed, data not loaded',
                  '- Data not yet loaded into database'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />

              <Divider orientation="left">5. Special Notes</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Highlights projects with unprocessed raw data',
                  'Explanation of processing issues (mainly lack of phenotypic information)'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <ApiOutlined /> Pathogenic Analysis Page
                </Title>
              }
              key="6"
            >
              <Paragraph>
                The Pathogenic Analysis page provides an overview of pathogenic bacteria ratios across all samples.
              </Paragraph>

              <Divider orientation="left">1. Pathogenic Sample Overview</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Table displaying each sample with Run ID, BioProject, Habitat, Country, and pathogenic ratio',
                  'Run ID is clickable to navigate to the Sample Detail page',
                  'BioProject links directly to NCBI BioProject database',
                  'Pathogenic ratio column is clickable to view detailed pathogenic species for that sample',
                  'Supports pagination with page-level caching for fast navigation',
                  'Export all data to Excel via the Export All button'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.pathogenicMain} style={{ maxWidth: '100%', margin: '16px 0' }} />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <FileSearchOutlined /> Pathogenic Detail Page
                </Title>
              }
              key="7"
            >
              <Paragraph>
                The Pathogenic Detail page shows all pathogenic species found in a specific sample, identified by the <Text code>is_pathogenic</Text> flag in the species table.
              </Paragraph>

              <Divider orientation="left">1. Pathogenic Species List</Divider>
              <List
                itemLayout="horizontal"
                dataSource={[
                  'Complete taxonomy information: Species ID, Species, Genus, Family, Order, Class, Phylum, Kingdom',
                  'Reads count for each pathogenic species in this sample',
                  'Species names link to the Species Detail page for further analysis',
                  'NCBI and ENA buttons provide quick access to external databases',
                  'Export all data to Excel via the Export All button'
                ]}
                renderItem={item => <List.Item>{item}</List.Item>}
              />
              <Image src={images.pathogenicDetail} style={{ maxWidth: '100%', margin: '16px 0' }} />
            </Panel>

            <Panel
              header={
                <Title level={4} style={{ margin: 0 }}>
                  <QuestionCircleOutlined /> Help Page
                </Title>
              }
              key="8"
            >
              <Paragraph>
                This page provides a basic introduction to the database and its key features.
              </Paragraph>
            </Panel>
          </Collapse>
        </div>
      </Content>
    </Layout>
  );
};

export default Help;
